import type { AppUser, Page } from '@servicetim/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { adminPath, useApiContext } from '../../../api/client';
import type { FieldControlProps } from '../field-control';
import type { OptionLoader } from '../types';
import { AsyncMultiPicker, AsyncPicker } from './async-picker';

const PICKER_PAGE_SIZE = 200;
const PICKER_RESULTS = 20;

/**
 * Default user loader: one cached page of `GET /admin/v1/users` (200 rows) filtered client-side
 * by display name / email. The users endpoint is tenant-admin only, so agents without that role
 * see an error in the picker until the API grows a lookup endpoint (tracked in the G1 report).
 */
export function useUserOptionLoader(): OptionLoader {
  const { request, keys } = useApiContext();
  const queryClient = useQueryClient();
  return useCallback(
    async (query) => {
      const page = await queryClient.fetchQuery({
        queryKey: keys.users.list({ pageSize: PICKER_PAGE_SIZE, mode: 'picker' }),
        queryFn: ({ signal }) =>
          request<Page<AppUser>>('GET', adminPath('/users'), {
            query: { pageSize: PICKER_PAGE_SIZE },
            signal,
          }),
        staleTime: 60_000,
      });
      const q = query.trim().toLowerCase();
      return page.items
        .filter((u) => u.status !== 'disabled')
        .filter(
          (u) =>
            q.length === 0 ||
            u.displayName.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q),
        )
        .slice(0, PICKER_RESULTS)
        .map((u) => ({ id: u.id, label: u.displayName, description: u.email }));
    },
    [keys, queryClient, request],
  );
}

/** `user` (and the system `assignee_id`): single async user picker. */
export function UserPickerControl(props: FieldControlProps) {
  const defaultLoader = useUserOptionLoader();
  const { field, value, onChange, id, disabled, invalid, describedBy, displayLabel, pickers } =
    props;
  return (
    <AsyncPicker
      id={id}
      value={typeof value === 'string' ? value : undefined}
      onChange={onChange}
      load={pickers?.users ?? defaultLoader}
      displayLabel={displayLabel}
      placeholder={field.placeholder ?? 'Select a user…'}
      searchPlaceholder="Search people…"
      disabled={disabled || field.readOnly}
      invalid={invalid}
      required={field.required}
      describedBy={describedBy}
    />
  );
}

/** `multi_user`: multi-value async user picker. */
export function MultiUserPickerControl(props: FieldControlProps) {
  const defaultLoader = useUserOptionLoader();
  const { field, value, onChange, id, disabled, invalid, describedBy, displayLabel, pickers } =
    props;
  const max = field.config?.type === 'multi_user' ? field.config.maxSelections : undefined;
  return (
    <AsyncMultiPicker
      id={id}
      value={Array.isArray(value) ? (value as string[]) : undefined}
      onChange={onChange}
      load={pickers?.users ?? defaultLoader}
      displayLabel={displayLabel}
      max={max}
      placeholder={field.placeholder ?? 'Select people…'}
      searchPlaceholder="Search people…"
      disabled={disabled || field.readOnly}
      invalid={invalid}
      required={field.required}
      describedBy={describedBy}
    />
  );
}
